import {fetchAuthTeam, guidesCountQueryOptions, teamsQueryOptions} from '@blesk-frontend/api';
import {useBleskAuth} from '@blesk-frontend/auth';
import {
  Blesk,
  Collections,
  Docs,
  Favorite,
  SidebarCount,
  SidebarGroup,
  SidebarLink,
  Trash,
} from '@blesk-frontend/ui';
import {setUser} from '@sentry/react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {Link, useNavigate} from '@tanstack/react-router';
import {useEffect} from 'react';
import {teamMemberInviteHintKey} from '../features/team';
import {updateExtensionToken} from '../libs';
import {AppSidebarPremium} from './AppSidebarPremium';
import {AppSidebarSearch} from './AppSidebarSearch';
import {AppSidebarTeamMenu} from './AppSidebarTeamMenu';
import {AppSidebarUser} from './AppSidebarUser';

export const AppSidebar = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    query: {data: user},
    teamId,
    isAdmin,
    updateToken,
    logout,
  } = useBleskAuth();

  const {data: count} = useQuery(guidesCountQueryOptions());
  const {data: teams} = useQuery(teamsQueryOptions());

  useEffect(() => {
    setUser({
      id: user?.id,
      email: user?.email,
      teamId,
    });
  }, [user, teamId]);

  const onClickTeamSwitch = async (teamId: string) => {
    const {accessToken} = await fetchAuthTeam({teamId});
    localStorage.removeItem(teamMemberInviteHintKey);
    updateToken(accessToken);
    queryClient.invalidateQueries({type: 'all'});
    queryClient.invalidateQueries({type: 'inactive'});
    updateExtensionToken(accessToken);
    navigate({to: '/'});
  };

  const onClickLogout = () => {
    logout();
    updateExtensionToken(null);
    location.pathname = '/auth/signin';
  };

  return (
    <div className="flex h-full flex-col p-4">
      <SidebarGroup className="mb-6 flex items-center justify-center p-4 pt-8">
        <Link to="/">
          <Blesk />
        </Link>
      </SidebarGroup>

      {teams && teams.length > 1 && (
        <SidebarGroup>
          <AppSidebarTeamMenu user={user} teams={teams} onClickTeamSwitch={onClickTeamSwitch} />
        </SidebarGroup>
      )}

      <SidebarGroup>
        <AppSidebarSearch />
      </SidebarGroup>

      <SidebarGroup className="flex flex-col gap-2">
        <SidebarLink to="/" activeOptions={{exact: true}}>
          <Docs className="size-6" />
          <span>Инструкции</span>
          <SidebarCount value={count?.unarchived} />
        </SidebarLink>
        <SidebarLink to="/" search={{favorites: true}}>
          <Favorite className="size-6" />
          <span>Избранное</span>
          <SidebarCount value={count?.favorites} />
        </SidebarLink>
        <SidebarLink to="/" search={{trash: true}} activeOptions={{exact: true}}>
          <Trash className="size-6" />
          <span>Корзина</span>
          <SidebarCount value={count?.archived} />
        </SidebarLink>
      </SidebarGroup>

      <div className="mb-6 border-b" />

      <SidebarGroup>
        <SidebarLink to="/collections">
          <Collections className="size-6" />
          <span>Коллекции</span>
          <SidebarCount value={count?.collections} />
        </SidebarLink>
      </SidebarGroup>

      <SidebarGroup>
        <AppSidebarPremium />
      </SidebarGroup>

      <div className="mt-auto">
        {isAdmin && (
          <div className="mb-2 rounded-md bg-error-primary px-3 py-2 text-paragraph-xs text-text-contrast">
            Доступ к команде под админом!
          </div>
        )}

        {user && <AppSidebarUser user={user} onClickLogout={onClickLogout} />}
      </div>
    </div>
  );
};
